import { apiClient } from "@/http";
import { apiRoutes } from "./apiRoutes";
import type { Embodiment } from "./embodiment";
import type { Label } from "./label";

/** 같은 Dataset membership·제외 조건으로 집계한 파일별 실제 헤더 통계다. */
export type DatasetCameraDetails = {
  camera: string;
  video_count: number;
  probed_video_count: number;
  resolutions: readonly { width: number; height: number; video_count: number }[];
  fps_values: readonly number[];
  codecs: readonly string[];
  total_frame_count: number | null;
  total_duration_seconds: number | null;
  file_count: number;
  total_size_bytes: number | null;
  mask_count: number;
};

export type DatasetDetails = {
  dataset_uuid: string;
  summary_label: string;
  summary_scope: string;
  // Embodiment 카탈로그의 단일 nested 프로필. arms/end_effector 를 소유한다.
  // 카메라 채널·스트림 수는 아래 Dataset 실측 지표가 소유한다.
  embodiment: Embodiment;
  environment: readonly Label[];
  source: readonly Label[];
  task: readonly Label[];
  // primary anchor source 의 schema 밖(extra) 파일 수다. 상세 개요 지표 바의 "추가 파일"이 표시한다.
  extra_file_count: number;
  total_size_bytes: number | null;
  total_duration_sec: number | null;
  episode_count: number;
  camera_stream_count: number;
  camera_stream_keys: readonly string[];
  // null은 아직 집계하지 않은 cache, []는 집계한 결과 카메라가 없음이다.
  // 카메라 상세 응답 배포 전에는 필드가 없다.
  // @compat ZERG-626 until=be:4ed593b0
  cameras?: readonly DatasetCameraDetails[] | null;
  median_episode_length_sec: number | null;
  primary_rate_hz: number | null;
  // 에피소드 품질 태그 롤업의 상위 K 개다(비중 내림차순). 위 라벨 facet 과 **별개 축**이다.
  // facet 은 데이터셋에 직접 부여한 값이다(렌즈 결함처럼 촬영 리그 단위).
  // 이 값은 에피소드 판정의 집계다. 같은 배지로 합치면 성격이 다른 두 값이 섞인다.
  // 그래서 표시도 분리한다.
  episode_quality_rollup: readonly EpisodeQualityRollup[];
  inspect_result: DatasetInspectResult;
};

// 데이터셋 안에서 품질 태그 하나가 차지하는 비중이다. `ratio` 의 분모는 `episode_count` 와
// 같은 모집단이다(전역 제외를 뺀 매핑 에피소드). 이 값은 존재 여부가 아니라 비율이다.
// 에피소드가 수천 개인 데이터셋에서는 "태그가 하나라도 있음" 이 거의 항상 참이기 때문이다.
export type EpisodeQualityRollup = {
  label: string;
  episode_count: number;
  ratio: number;
};

export type DatasetInspectResult = {
  hatchery_data_version?: string;
  meta?: DatasetMetaInspect;
  warnings?: readonly DatasetInspectWarning[];
};

export type DatasetMetaInspect = {
  status?: "present" | "reconstructed";
  framerate?: number | null;
};

export type DatasetInspectWarning = {
  code: string;
  severity: "info" | "warning";
  detail: string;
  // 서버는 대량 경고의 샘플만 반환한다. 이 값은 해당 코드의 전체 건수다.
  total_count?: number;
};

export async function getDatasetDetails(datasetUuid: string): Promise<DatasetDetails> {
  const response = await apiClient.get<DatasetDetails>(apiRoutes.dataset.details(datasetUuid));
  return response.data;
}

export async function refreshDatasetDetails(datasetUuid: string): Promise<DatasetDetails> {
  const response = await apiClient.post<DatasetDetails>(
    apiRoutes.dataset.detailsRefresh(datasetUuid),
  );
  return response.data;
}
